import { MULTIPLE_KEY } from '@web-platform/shared-universal-constants/src/carrierTypes';

import { NONE_INTENT_KEY } from './noneIntent';

import type { EnrichedCarrierEntry, EnrichedCarriers } from './selectors';
import type { Alliance } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/alliances';
import type {
  Carrier,
  CarrierMultipleAggregate,
} from '@web-platform/shared-universal-types/src/redux/state/entities/flights/carriers';
import type { AlliancesFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/alliances';
import type { CarriersFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/carriers';

export const parseNumericToken = (token: string): number | null => {
  if (!/^-?\d+$/.test(token)) {
    return null;
  }
  const parsed = Number.parseInt(token, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const findCarrierByDisplayCode = (
  byId: Record<number, Carrier>,
  displayCode: string,
): Carrier | undefined =>
  Object.values(byId).find((carrier) => carrier.display_code === displayCode);

export const inferDefaultCarrierValue = (state: CarriersFilterState): boolean => {
  const anyExplicitPositive = Object.values(state).some(
    (entry) => entry.inferred !== true && entry.value === true,
  );
  return !anyExplicitPositive;
};

export const inferDefaultCarrierValueResolvable = (
  state: CarriersFilterState,
  byId: Record<number, Carrier>,
): boolean => {
  const anyResolvablePositive = Object.entries(state).some(([token, entry]) => {
    if (entry.inferred === true || entry.value !== true) return false;
    if (token === MULTIPLE_KEY || token === NONE_INTENT_KEY) return true;
    const numeric = parseNumericToken(token);
    if (numeric !== null && byId[numeric]) return true;
    return findCarrierByDisplayCode(byId, token) !== undefined;
  });
  return !anyResolvablePositive;
};

// Returns the set of carrier ids already represented in state, regardless of
// whether the state token is the numeric id or the legacy display_code form.
const collectRepresentedCarrierIds = (
  state: CarriersFilterState,
  byId: Record<number, Carrier>,
): Set<number> => {
  const ids = new Set<number>();
  Object.keys(state).forEach((token) => {
    if (token === MULTIPLE_KEY) return;
    const numeric = parseNumericToken(token);
    if (numeric !== null && byId[numeric]) {
      ids.add(numeric);
      return;
    }
    const carrier = findCarrierByDisplayCode(byId, token);
    if (carrier) {
      ids.add(carrier.id);
    }
  });
  return ids;
};

export const reconcileCarriersFilterState = ({
  byId,
  currentResultIds,
  inferDefault: inferDefaultOverride,
  multiple,
  multipleFollowsInferDefault = false,
  state,
}: {
  state: CarriersFilterState;
  byId: Record<number, Carrier>;
  currentResultIds: number[];
  inferDefault?: boolean;
  multiple: CarrierMultipleAggregate | null;
  multipleFollowsInferDefault?: boolean;
}): CarriersFilterState => {
  const inferDefault = inferDefaultOverride ?? inferDefaultCarrierValueResolvable(state, byId);
  const representedIds = collectRepresentedCarrierIds(state, byId);

  const additions: CarriersFilterState = {};
  currentResultIds.forEach((id) => {
    if (!byId[id] || representedIds.has(id)) return;
    additions[String(id)] = { value: inferDefault, inferred: true };
  });

  if (multiple && state[MULTIPLE_KEY] === undefined) {
    const multipleValue = multipleFollowsInferDefault ? inferDefault : true;
    additions[MULTIPLE_KEY] = { value: multipleValue, inferred: true };
  }

  if (Object.keys(additions).length === 0) {
    return state;
  }
  return { ...state, ...additions };
};

export const getCarrierUrlToken = (carrier: EnrichedCarrierEntry) =>
  carrier.id === MULTIPLE_KEY ? MULTIPLE_KEY : String(carrier.id);

// AlliancesFilterState is keyed by alliance name; carrier.alliance is a numeric id.
export const getEnabledAllianceIds = (
  alliances: AlliancesFilterState,
  alliancesById: Record<number, Alliance> | undefined,
): Set<number> => {
  const enabledNames = new Set(
    Object.entries(alliances)
      .filter(([, entry]) => entry.value === true)
      .map(([name]) => name),
  );
  const ids = new Set<number>();
  if (enabledNames.size === 0 || !alliancesById) {
    return ids;
  }
  Object.values(alliancesById).forEach((alliance) => {
    if (enabledNames.has(alliance.name)) {
      ids.add(alliance.id);
    }
  });
  return ids;
};

export const getDeselectedCarrierNames = (
  carriers: EnrichedCarriers,
  selectedCarrier: EnrichedCarrierEntry,
) =>
  Object.values(carriers)
    .filter((carrierItem) => carrierItem.id !== selectedCarrier.id)
    .map((carrierItem) => carrierItem.name)
    .join(',');
